/**
 * Metlas Intelligence widget.
 *
 * A floating chat launcher (bottom-right) that expands into an admin chat which
 * audits the advising bot's answer quality over a QA-log window. The date window
 * (From / To) is chosen inside the chat header; the program filter comes from the
 * overview page. Streams Sonnet 4.6 responses over SSE and shows a lightweight
 * indicator while the model runs its read-only QA-DB tools.
 *
 * Styled to match the admin panel's dark theme (see AdvisorAdmin.css). The
 * conversation persists across open/close (the widget stays mounted).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  advisorApiService,
  type MetlasStreamEvent,
} from "../services/advisor-api";
import { MarkdownMessage } from "./MarkdownMessage";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Inject the typing-indicator keyframes once.
if (
  typeof document !== "undefined" &&
  !document.getElementById("metlas-widget-styles")
) {
  const el = document.createElement("style");
  el.id = "metlas-widget-styles";
  el.textContent =
    "@keyframes metlasBounce{0%,80%,100%{transform:translateY(0);opacity:.35}40%{transform:translateY(-3px);opacity:1}}" +
    ".metlas-typing{display:inline-flex;gap:4px;align-items:center}" +
    ".metlas-typing i{width:6px;height:6px;border-radius:50%;background:#a1a1aa;display:inline-block;animation:metlasBounce 1.2s infinite}" +
    ".metlas-typing i:nth-child(2){animation-delay:.15s}.metlas-typing i:nth-child(3){animation-delay:.3s}" +
    ".metlas-suggestion{display:block;width:100%;text-align:left;margin:0;padding:10px 12px;border:1px solid rgba(255,255,255,0.14);border-radius:10px;background:#23232f;color:#c7c7cf;cursor:pointer;font-size:13px;transition:background .12s,border-color .12s,color .12s}" +
    ".metlas-suggestion:hover{background:#2b2b3b;border-color:rgba(99,102,241,0.6);color:#e4e4e7}";
  document.head.appendChild(el);
}

const TypingDots: React.FC = () => (
  <span className="metlas-typing">
    <i />
    <i />
    <i />
  </span>
);

interface MetlasIntelligencePanelProps {
  /** Program filter from the overview page, or null for all programs. */
  programCode: string | null;
}

/** YYYY-MM-DD for a Date offset from now by `days`. */
const dateOffset = (days: number): string =>
  new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);

const SUGGESTIONS = [
  "What are the categories in this window, with a success rate for each?",
  "How many answers were reasonable vs the total?",
  "Which failures are genuine content gaps vs retrieval misses?",
];

export const MetlasIntelligencePanel: React.FC<
  MetlasIntelligencePanelProps
> = ({ programCode }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  // From (inclusive) / To (exclusive → default tomorrow to include today).
  const [startDate, setStartDate] = useState<string>(dateOffset(-30));
  const [endDate, setEndDate] = useState<string>(dateOffset(1));
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sessionIdRef = useRef<string | undefined>(undefined);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Floating-card size (resizable from the top-left grip; the card is pinned to
  // the bottom-right corner, so it grows up/left and stays on screen).
  const [size, setSize] = useState({ w: 400, h: 560 });

  const rangeValid = Boolean(startDate && endDate && endDate > startDate);

  const startResize = (e: React.PointerEvent): void => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.w;
    const startH = size.h;
    const onMove = (ev: PointerEvent): void => {
      setSize({
        w: Math.min(
          Math.max(startW + (startX - ev.clientX), 320),
          window.innerWidth - 40,
        ),
        h: Math.min(
          Math.max(startH + (startY - ev.clientY), 380),
          window.innerHeight - 40,
        ),
      });
    };
    const onUp = (): void => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  useEffect(() => {
    if (open) {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, toolStatus, open]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming || !rangeValid) return;
      setError(null);
      setInput("");

      const history = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      setMessages([
        ...messages,
        { role: "user", content: trimmed },
        { role: "assistant", content: "" },
      ]);
      setIsStreaming(true);
      setToolStatus(null);

      const controller = new AbortController();
      abortRef.current = controller;

      let assistantText = "";
      const setAssistant = (value: string) => {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: value };
          return copy;
        });
      };

      const onEvent = (event: MetlasStreamEvent): void => {
        switch (event.type) {
          case "session":
            sessionIdRef.current = event.session_id;
            break;
          case "text":
            assistantText += event.delta;
            setAssistant(assistantText);
            break;
          case "tool_start":
            setToolStatus(
              event.name === "fetch_selected_log"
                ? "Reading the selected log…"
                : "Querying the QA database…",
            );
            break;
          case "tool_result":
            setToolStatus(null);
            break;
          case "done":
            if (event.reply && event.reply !== assistantText) {
              assistantText = event.reply;
              setAssistant(assistantText);
            }
            break;
          case "error":
            setError(event.message);
            break;
        }
      };

      try {
        await advisorApiService.streamMetlasIntelligence(
          {
            message: trimmed,
            start_date: startDate,
            end_date: endDate,
            program_code: programCode ?? undefined,
            history,
            session_id: sessionIdRef.current,
          },
          onEvent,
          controller.signal,
        );
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(
            err instanceof Error ? err.message : "Something went wrong.",
          );
        }
      } finally {
        setIsStreaming(false);
        setToolStatus(null);
        abortRef.current = null;
      }
    },
    [messages, isStreaming, rangeValid, startDate, endDate, programCode],
  );

  // Clear is a purely client-side reset: abort any in-flight stream, empty the
  // transcript, and start a fresh session. Logged turns are retained server-side.
  const clearChat = (): void => {
    abortRef.current?.abort();
    abortRef.current = null;
    sessionIdRef.current = undefined;
    setMessages([]);
    setInput("");
    setError(null);
    setToolStatus(null);
    setIsStreaming(false);
  };

  // Collapsed: floating launcher with a hover label.
  if (!open) {
    return (
      <div style={styles.launcherWrap}>
        {hovered && <div style={styles.launcherLabel}>Metlas Intelligence</div>}
        <button
          style={{
            ...styles.launcher,
            ...(hovered ? styles.launcherHover : {}),
          }}
          onClick={() => setOpen(true)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          aria-label="Open Metlas Intelligence"
          title="Metlas Intelligence"
        >
          ✨
        </button>
      </div>
    );
  }

  // Expanded: floating, resizable chat card (no page-blocking overlay).
  return (
    <div style={{ ...styles.panel, width: size.w, height: size.h }}>
      <div
        style={styles.resizeGrip}
        onPointerDown={startResize}
        title="Drag to resize"
      />
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.title}>✨ Metlas Intelligence</div>
          <div style={styles.headerActions}>
            <button
              style={styles.clear}
              onClick={clearChat}
              disabled={messages.length === 0 && !isStreaming}
              title="Clear this conversation"
            >
              Clear
            </button>
            <button
              style={styles.close}
              onClick={() => setOpen(false)}
              aria-label="Minimize"
            >
              ×
            </button>
          </div>
        </div>
        <div style={styles.rangeRow}>
          <label style={styles.rangeLabel}>
            From
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={styles.dateInput}
            />
          </label>
          <label style={styles.rangeLabel}>
            To
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={styles.dateInput}
            />
          </label>
          {programCode && <span style={styles.programChip}>{programCode}</span>}
        </div>
        {!rangeValid && (
          <div style={styles.rangeHint}>“To” must be after “From”.</div>
        )}
      </div>

      <div ref={scrollRef} style={styles.messages}>
        {messages.length === 0 && (
          <div style={styles.empty}>
            <p style={{ marginTop: 0 }}>
              Ask about the advising bot&apos;s performance over the selected
              window. I can classify topics, judge answer quality, and query the
              QA database to tell a retrieval miss apart from a genuine content
              gap.
            </p>
            <div style={styles.suggestionList}>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  className="metlas-suggestion"
                  onClick={() => void send(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => {
          const isLast = i === messages.length - 1;
          const isThinking =
            m.role === "assistant" && !m.content && isStreaming && isLast;
          return (
            <div
              key={i}
              style={{
                ...styles.bubble,
                ...(m.role === "user"
                  ? styles.userBubble
                  : styles.assistantBubble),
              }}
            >
              {m.role === "assistant" ? (
                isThinking ? (
                  <TypingDots />
                ) : (
                  <MarkdownMessage content={m.content} />
                )
              ) : (
                m.content
              )}
            </div>
          );
        })}
        {toolStatus && <div style={styles.toolStatus}>{toolStatus}</div>}
        {error && <div style={styles.error}>{error}</div>}
      </div>

      <form
        style={styles.inputRow}
        onSubmit={(e) => {
          e.preventDefault();
          void send(input);
        }}
      >
        <input
          style={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about this window…"
          disabled={isStreaming}
        />
        <button
          style={{
            ...styles.sendButton,
            ...(isStreaming || !input.trim() || !rangeValid
              ? styles.sendButtonDisabled
              : {}),
          }}
          type="submit"
          disabled={isStreaming || !input.trim() || !rangeValid}
        >
          {isStreaming ? "…" : "Send"}
        </button>
      </form>
    </div>
  );
};

// Palette mirrors AdvisorAdmin.css: bg #0f0f14, card #1e1e2a, input #1a1a24,
// text #e4e4e7 / #a1a1aa / #71717a, indigo accent #6366f1 (hover #4f52d3).
const BORDER = "rgba(255,255,255,0.08)";
const styles: Record<string, React.CSSProperties> = {
  // Launcher (collapsed)
  launcherWrap: {
    position: "fixed",
    right: 24,
    bottom: 24,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  launcherLabel: {
    background: "#1e1e2a",
    color: "#e4e4e7",
    fontSize: 13,
    fontWeight: 600,
    padding: "6px 10px",
    borderRadius: 7,
    whiteSpace: "nowrap",
    border: `1px solid ${BORDER}`,
    boxShadow: "0 2px 10px rgba(0,0,0,0.4)",
  },
  launcher: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    border: "none",
    background: "#6366f1",
    color: "#fff",
    fontSize: 24,
    cursor: "pointer",
    boxShadow: "0 4px 16px rgba(99,102,241,0.45)",
    transition:
      "transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
  },
  launcherHover: {
    transform: "scale(1.08)",
    background: "#4f52d3",
    boxShadow: "0 6px 22px rgba(99,102,241,0.6)",
  },

  // Floating chat card (expanded) — pinned bottom-right, does not cover the page.
  panel: {
    position: "fixed",
    right: 24,
    bottom: 24,
    maxWidth: "calc(100vw - 40px)",
    maxHeight: "calc(100vh - 40px)",
    background: "#0f0f14",
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
    color: "#e4e4e7",
    overflow: "hidden",
    zIndex: 1001,
  },
  resizeGrip: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 18,
    height: 18,
    cursor: "nwse-resize",
    // Two faint diagonal ticks hint the drag affordance.
    background:
      "linear-gradient(135deg, transparent 45%, rgba(255,255,255,0.35) 46%, rgba(255,255,255,0.35) 52%, transparent 53%)",
    zIndex: 3,
  },
  header: { padding: "14px 18px", borderBottom: `1px solid ${BORDER}` },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { fontSize: 17, fontWeight: 700, color: "#e4e4e7" },
  headerActions: { display: "flex", alignItems: "center", gap: 6 },
  clear: {
    border: `1px solid ${BORDER}`,
    background: "transparent",
    color: "#a1a1aa",
    fontSize: 12,
    fontWeight: 600,
    padding: "5px 10px",
    borderRadius: 7,
    cursor: "pointer",
  },
  close: {
    border: "none",
    background: "none",
    fontSize: 24,
    cursor: "pointer",
    color: "#71717a",
    lineHeight: 1,
  },
  rangeRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 10,
    marginTop: 10,
    flexWrap: "wrap",
  },
  rangeLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 11,
    color: "#71717a",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  dateInput: {
    background: "#1a1a24",
    color: "#e4e4e7",
    border: `1px solid ${BORDER}`,
    borderRadius: 7,
    padding: "6px 8px",
    fontSize: 13,
    colorScheme: "dark",
  },
  programChip: {
    background: "rgba(99,102,241,0.12)",
    color: "#a5b4fc",
    borderRadius: 6,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 1,
  },
  rangeHint: { marginTop: 6, fontSize: 12, color: "#f87171" },

  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  empty: { color: "#a1a1aa", fontSize: 14, lineHeight: 1.5 },
  suggestionList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 12,
  },
  bubble: {
    maxWidth: "90%",
    padding: "11px 13px",
    borderRadius: 14,
    fontSize: 14,
    whiteSpace: "pre-wrap",
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  userBubble: {
    alignSelf: "flex-end",
    background: "#6366f1",
    color: "#fff",
    borderBottomRightRadius: 4,
  },
  assistantBubble: {
    alignSelf: "flex-start",
    maxWidth: "93%",
    background: "#23232f",
    color: "#e4e4e7",
    border: "1px solid rgba(255,255,255,0.14)",
    borderBottomLeftRadius: 4,
    boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
    whiteSpace: "normal",
  },
  toolStatus: {
    alignSelf: "flex-start",
    fontSize: 12,
    color: "#71717a",
    fontStyle: "italic",
  },
  error: {
    alignSelf: "stretch",
    fontSize: 13,
    color: "#f87171",
    background: "rgba(248,113,113,0.1)",
    padding: "8px 10px",
    borderRadius: 8,
  },
  inputRow: {
    display: "flex",
    gap: 8,
    padding: "12px 16px",
    borderTop: `1px solid ${BORDER}`,
  },
  input: {
    flex: 1,
    padding: "10px 12px",
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    fontSize: 14,
    background: "#1a1a24",
    color: "#e4e4e7",
  },
  sendButton: {
    padding: "10px 16px",
    border: "none",
    borderRadius: 8,
    background: "#6366f1",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  sendButtonDisabled: {
    background: "#27272a",
    color: "#71717a",
    cursor: "not-allowed",
  },
};
